import { AlertTriangle, RefreshCw } from "lucide-react";
import { Component, ReactNode } from "react";

import { Button } from "./Button";
import { Card } from "./Card";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: any;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: any) {
    this.setState({ error, errorInfo });

    // Log to console in development
    if (import.meta.env?.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  private handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      // Custom fallback component
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="max-w-md w-full">
            <div className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-red-100 p-3">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>

              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Something went wrong
              </h3>

              <p className="text-text-secondary mb-6">
                {import.meta.env?.DEV && this.state.error
                  ? this.state.error.message
                  : "We encountered an unexpected error. Please try again."}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={this.handleRetry}
                  leftIcon={<RefreshCw className="h-4 w-4" />}
                >
                  Try Again
                </Button>

                <Button variant="primary" onClick={this.handleReload}>
                  Reload Page
                </Button>
              </div>

              {/* Development error details */}
              {import.meta.env?.DEV &&
                this.state.error && (
                  <details className="mt-6 text-left">
                    <summary className="text-sm font-medium cursor-pointer text-text-secondary hover:text-text-primary">
                      Error Details (Development)
                    </summary>
                    <pre className="mt-2 p-3 bg-background-tertiary rounded text-xs overflow-auto">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
